import { Breakpoints, Media } from '../../media/mediaQueries.js';
import { TextLineHeight } from './constants.js';
import styled, { css } from 'styled-components';
import { getResolvedWeight } from '../getResolvedWeight.js';
import { returnFontFamilyValues } from './utils.js';
import { TextOverflow, TextDecoration } from './types.js';

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }

// Only fallback to the overflow prop of styles
// if the prop passed is undefined
var overflowIsTypeForSizeGroup = function overflowIsTypeForSizeGroup(sizeGroup) {
  return function (props, type) {
    return (// TO-DO: fix this typing after we remove legacy styles in v8
      props.overflow === type || props.overflow === undefined && props[sizeGroup].overflow === type
    );
  };
}; // We can set letterSpacing to 0.0,
// so we need to check on that condition before
// falling back to the styles letterSpacing value


var getLetterSpacingForSizeGroup = function getLetterSpacingForSizeGroup(sizeGroup) {
  return function (props) {
    if (props.letterSpacing === 0 || props.letterSpacing === 0.0) {
      return 0;
    }

    var group = props[sizeGroup];
    var sizeGroupLetterSpacing = group && typeof group !== 'string' && group.letterSpacing; // casting this for now to not mess with these conditionals too much and keep current behavior

    return props.letterSpacing || props.letterSpacing === 0.0 || sizeGroupLetterSpacing;
  };
};

var getLineHeightForSizeGroup = function getLineHeightForSizeGroup(sizeGroup) {
  return function (props) {
    var group = props[sizeGroup];
    var sizeGroupLineHeight = group && typeof group !== 'string' && group.lineHeight;
    var sizeGroupSize = group && typeof group !== 'string' && group.size;
    var lineHeight = props.lineHeight || sizeGroupLineHeight;
    var size = props.size || sizeGroupSize; // In the legacy typography system, `lineHeight` values were
    // defined as relative multipliers (e.g. `em` values) that we then
    // calculated with the below rounding logic to be a desired pixel value.
    // In the modern system, the `lineHeight` values are represented in pixel
    // values, not relative multipliers. The below check sees if the size times
    // lineHeight is less than or equal to the `S10` line height, which is the largest
    // line height available in the legacy system.
    // If true, we can assume it is a legacy multiplier value and use the multiplication
    // and rounding logic to create a pixel equivalent for the lineHeight property.
    // If false, we can assume it is a modern value, and pass back the original value.

    if (size && lineHeight && size > lineHeight && size * lineHeight <= size * TextLineHeight.S10) {
      var MULTIPLE_TO_ROUND_TO = 2; // We round our lineHeights to the nearest 4pt
      // multiple—ensures that our vertical rhythm is
      // more consistent paired with our spacing values

      return Math.round(MULTIPLE_TO_ROUND_TO * Math.round(size * lineHeight / MULTIPLE_TO_ROUND_TO));
    }

    return lineHeight;
  };
};

var getDecorationForSizeGroup = function getDecorationForSizeGroup(sizeGroup) {
  return function (props) {
    var decorationVal = ''; // TO-DO: fix this typing after we remove legacy styles in v8

    var decoration = props.textDecoration || props[sizeGroup].textDecoration;
    var decorationColor = props.themeDecorationColor;

    if (Array.isArray(decoration)) {
      decorationVal = decoration.join(' ');
    } else {
      decorationVal = decoration;
    }

    if (decorationVal && decorationVal.indexOf(TextDecoration.None) > -1) {
      return TextDecoration.None;
    }

    if (decorationColor) {
      decorationVal = "".concat(decorationVal, " ").concat(decorationColor);
    }

    return decorationVal;
  };
};

var getWeightForSizeGroup = function getWeightForSizeGroup(sizeGroup) {
  return function (props) {
    var group = props[sizeGroup];
    var sizeGroupWeight = group && typeof group !== 'string' && group.weight;
    var specifiedWeight = props.weight || sizeGroupWeight;
    return getResolvedWeight(props.themeFontFamily, specifiedWeight);
  };
};

var getOverrideOrStyleForSizeGroup = function getOverrideOrStyleForSizeGroup(sizeGroup) {
  return function (property) {
    return function (props) {
      // TO-DO: fix this typing after we remove legacy styles in v8
      return props[property] || props[sizeGroup][property];
    };
  };
};
/**
 * Defines a CSS fragment with typography styles based on the set needed for the media query.
 * @param sizeGroup - Defines what group of styles in props to generate a CSS fragment with.
 */


var getStylesForSizeGroup = function getStylesForSizeGroup(sizeGroup) {
  var getOverrideOrStyle = getOverrideOrStyleForSizeGroup(sizeGroup);
  var getLineHeight = getLineHeightForSizeGroup(sizeGroup);
  var getWeight = getWeightForSizeGroup(sizeGroup);
  var getLetterSpacing = getLetterSpacingForSizeGroup(sizeGroup);
  var getTextDecoration = getDecorationForSizeGroup(sizeGroup);
  var overflowIsType = overflowIsTypeForSizeGroup(sizeGroup);
  return css(["font-size:", "px;font-family:", ";font-weight:", ";line-height:", "px;letter-spacing:", "ch;text-transform:", ";text-decoration:", ";color:", ";text-align:", ";margin:0;padding:0;display:", ";font-variant-ligatures:no-common-ligatures;", ";", ";"], getOverrideOrStyle('size'), function (props) {
    var familyKey = getOverrideOrStyle('family')(props);
    return returnFontFamilyValues(props.themeFontFamily)[familyKey];
  }, getWeight, getLineHeight, getLetterSpacing, getOverrideOrStyle('textTransform'), getTextDecoration, function (props) {
    return props.textColor;
  }, getOverrideOrStyle('textAlign'), function (props) {
    return props.display;
  }, function (props) {
    return overflowIsType(props, TextOverflow.BreakWord) && css(["hyphens:auto;word-wrap:break-word;"]);
  }, function (props) {
    return overflowIsType(props, TextOverflow.Truncate) && css(["max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;", ";"], props.maxLines && props.maxLines > 1 && "\n            display: -webkit-box;\n            -webkit-line-clamp: ".concat(props.maxLines, ";\n            -webkit-box-orient: vertical;\n            max-height: ").concat(getLineHeight(props) * props.maxLines, "px;\n            white-space: normal;\n          "));
  });
};
var TextElement = styled.span.withConfig({
  displayName: "styles__TextElement",
  componentId: "sc-3qedjx-0"
})(["", " @media (min-width:", "px){", ";}", " ", " ", " ", ""], getStylesForSizeGroup('styles'), Breakpoints.Mobile + 1, function (props) {
  // Only used in the non-legacy system (from `StyledBaseText`
  // ), these sets of styles are used when DesktopWidth values
  // are defined in the given theme's TextStyle semantic. We want
  // to apply these four potential properties that are defined
  // in the theme for > Breakpoint.Mobile viewports, but we still
  // want to honor direct API prop overrides when they are defined.
  return props.themeDesktopOverrideValues && css(["", ";", ";", ";", ";"], props.themeDesktopOverrideValues.lineHeight && "line-height: ".concat(props.lineHeight || props.themeDesktopOverrideValues.lineHeight, "px;"), props.themeDesktopOverrideValues.size && "font-size: ".concat(props.size || props.themeDesktopOverrideValues.size, "px;"), props.themeDesktopOverrideValues.weight && "font-weight: ".concat(getResolvedWeight(props.themeFontFamily, props.weight || props.themeDesktopOverrideValues.weight), ";"), props.themeDesktopOverrideValues.letterSpacing && "letter-spacing: ".concat(props.letterSpacing || props.themeDesktopOverrideValues.letterSpacing, "ch;"));
}, function (props) {
  return props.wideScreenStyles && Media.WideScreen(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    ", "\n  "])), getStylesForSizeGroup('wideScreenStyles'));
}, function (props) {
  return props.desktopStyles && Media.Desktop(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n  "])), getStylesForSizeGroup('desktopStyles'));
}, function (props) {
  return props.tabletStyles && Media.Tablet(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    ", "\n  "])), getStylesForSizeGroup('tabletStyles'));
}, function (props) {
  return props.mobileStyles && Media.Mobile(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    ", "\n  "])), getStylesForSizeGroup('mobileStyles'));
});

export { TextElement, getStylesForSizeGroup };
